"use client";

import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Sheet,
  SheetContent,
} from "@/components/ui/overlays";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  createDocumentAction,
  deleteDocumentAction,
} from "@/actions/work-actions";
import { cn } from "@/lib/utils";
import {
  Search,
  Plus,
  FileCheck,
  FileText,
  Receipt,
  FileSignature,
  Calendar,
  ExternalLink,
  Trash2,
  BookOpen,
  Filter,
  Layers,
  FolderOpen,
  Network,
  Edit2,
} from "lucide-react";

interface DocumentItem {
  id: string;
  title: string;
  content: string;
  type: "contract" | "proposal" | "invoice_pdf" | "meeting_note";
  fileUrl: string | null;
  createdAt: string;
}

function renderDocumentContent(content: string | null, type: string) {
  if (!content) return "Nessun contenuto disponibile.";
  if (type === "proposal" && content.trim().startsWith("{")) {
    try {
      const parsed = JSON.parse(content);
      return (
        <div className="space-y-1.5 text-xs text-text-secondary">
          {parsed.description && (
            <p className="text-text-secondary leading-relaxed">
              {parsed.description}
            </p>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[11px] text-text-muted font-medium bg-card-muted/30 p-2 rounded border border-border/30">
            {parsed.clientName && (
              <span>
                <strong>Cliente:</strong> {parsed.clientName}
              </span>
            )}
            {parsed.value && (
              <span>
                <strong>Valore:</strong> €
                {Number(parsed.value).toLocaleString("it-IT")}
              </span>
            )}
            {parsed.status && (
              <span className="capitalize">
                <strong>Stato:</strong> {parsed.status}
              </span>
            )}
          </div>
        </div>
      );
    } catch (_) {}
  }
  return content;
}

interface DocumentsClientProps {
  initialDocuments: DocumentItem[];
}

export function DocumentsClient({ initialDocuments }: DocumentsClientProps) {
  const router = useRouter();
  const [documentsList, setDocumentsList] =
    useState<DocumentItem[]>(initialDocuments);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  // State for modals/sheets
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [inspectDoc, setInspectDoc] = useState<DocumentItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Create Form State
  const [newTitle, setNewTitle] = useState("");
  const [newType, setNewType] = useState<DocumentItem["type"]>("meeting_note");
  const [newContent, setNewContent] = useState("");
  const [newFileUrl, setNewFileUrl] = useState("");

  // Sync state with server props
  useEffect(() => {
    setDocumentsList(initialDocuments);
  }, [initialDocuments]);

  const handleOpenCreateModal = () => {
    setNewTitle("");
    setNewType("meeting_note");
    setNewContent("");
    setNewFileUrl("");
    setError("");
    setIsCreateOpen(true);
  };

  const handleCreateDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!newTitle.trim()) {
      setError("Il titolo del documento è obbligatorio.");
      return;
    }
    if (!newContent.trim()) {
      setError(
        "Il contenuto del documento è obbligatorio per l'indicizzazione RAG.",
      );
      return;
    }

    setLoading(true);
    try {
      const res = await createDocumentAction({
        title: newTitle,
        type: newType,
        content: newContent,
        fileUrl: newFileUrl || null,
      });

      if (res.error) {
        setError(res.error);
      } else {
        setIsCreateOpen(false);
        router.refresh();
      }
    } catch (err: any) {
      setError(err.message || "Errore durante il caricamento del documento.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation(); // Avoid opening inspect sheet when clicking delete
    if (
      !confirm(
        "Sei sicuro di voler eliminare definitivamente questo documento?",
      )
    )
      return;

    try {
      const res = await deleteDocumentAction(id);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert("Errore durante l'eliminazione.");
    }
  };

  // Filter documents
  const filteredDocuments = documentsList.filter((doc) => {
    const matchesSearch =
      doc.title.toLowerCase().includes(search.toLowerCase()) ||
      doc.content.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === "all" || doc.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const handleToggleTypeFilter = (type: string) => {
    setTypeFilter(type);
  };

  // Analytics Metrics
  const totalCount = documentsList.length;
  const contractCount = documentsList.filter(
    (d) => d.type === "contract",
  ).length;
  const proposalCount = documentsList.filter(
    (d) => d.type === "proposal",
  ).length;
  const invoiceCount = documentsList.filter(
    (d) => d.type === "invoice_pdf",
  ).length;
  const meetingCount = documentsList.filter(
    (d) => d.type === "meeting_note",
  ).length;

  // Render icons based on document type
  const getDocTypeIcon = (type: DocumentItem["type"]) => {
    switch (type) {
      case "contract":
        return <FileCheck className="h-5 w-5 text-error" />;
      case "proposal":
        return <FileSignature className="h-5 w-5 text-primary-500" />;
      case "invoice_pdf":
        return <Receipt className="h-5 w-5 text-success" />;
      case "meeting_note":
        return <FileText className="h-5 w-5 text-warning" />;
    }
  };

  const getDocTypeBadge = (type: DocumentItem["type"]) => {
    switch (type) {
      case "contract":
        return <Badge variant="error">Contratto</Badge>;
      case "proposal":
        return <Badge variant="info">Proposta</Badge>;
      case "invoice_pdf":
        return <Badge variant="success">Fattura PDF</Badge>;
      case "meeting_note":
        return <Badge variant="warning">Verbale Riunione</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Category breakdown header */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card
          onClick={() => setTypeFilter("all")}
          className={cn(
            "p-3 text-center flex flex-col justify-center cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-95 transition-all select-none",
            typeFilter === "all"
              ? "ring-1 ring-primary-600/50 bg-primary-600/[0.02] border-primary-500/50"
              : "border-border bg-card",
          )}
        >
          <span className="text-[10px] font-bold text-text-secondary uppercase tracking-wider">
            Totale File
          </span>
          <p className="text-xl font-bold mt-1 text-primary-600">
            {totalCount}
          </p>
        </Card>

        <Card
          onClick={() => handleToggleTypeFilter("contract")}
          className={cn(
            "p-3 text-center flex flex-col justify-center border-t-2 border-t-error cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-95 transition-all select-none",
            typeFilter === "contract"
              ? "ring-1 ring-error/50 bg-error/[0.02] border-error"
              : "border-border bg-card",
          )}
        >
          <span className="text-[10px] font-bold text-text-secondary uppercase tracking-wider">
            Contratti
          </span>
          <p className="text-xl font-bold mt-1 text-error">{contractCount}</p>
        </Card>

        <Card
          onClick={() => handleToggleTypeFilter("proposal")}
          className={cn(
            "p-3 text-center flex flex-col justify-center border-t-2 border-t-primary-500 cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-95 transition-all select-none",
            typeFilter === "proposal"
              ? "ring-1 ring-primary-500/50 bg-primary-500/[0.02] border-primary-500"
              : "border-border bg-card",
          )}
        >
          <span className="text-[10px] font-bold text-text-secondary uppercase tracking-wider">
            Proposte
          </span>
          <p className="text-xl font-bold mt-1 text-primary-600">
            {proposalCount}
          </p>
        </Card>

        <Card
          onClick={() => handleToggleTypeFilter("invoice_pdf")}
          className={cn(
            "p-3 text-center flex flex-col justify-center border-t-2 border-t-success cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-95 transition-all select-none",
            typeFilter === "invoice_pdf"
              ? "ring-1 ring-success/50 bg-success/[0.02] border-success"
              : "border-border bg-card",
          )}
        >
          <span className="text-[10px] font-bold text-text-secondary uppercase tracking-wider">
            Fatture
          </span>
          <p className="text-xl font-bold mt-1 text-success">{invoiceCount}</p>
        </Card>

        <Card
          onClick={() => handleToggleTypeFilter("meeting_note")}
          className={cn(
            "p-3 text-center flex flex-col justify-center border-t-2 border-t-warning col-span-2 md:col-span-1 cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-95 transition-all select-none",
            typeFilter === "meeting_note"
              ? "ring-1 ring-warning/50 bg-warning/[0.02] border-warning"
              : "border-border bg-card",
          )}
        >
          <span className="text-[10px] font-bold text-text-secondary uppercase tracking-wider">
            Verbali
          </span>
          <p className="text-xl font-bold mt-1 text-warning">{meetingCount}</p>
        </Card>
      </div>

      {/* Control Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-4 rounded-xl border border-border">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Cerca nei documenti..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex w-full sm:w-auto gap-3 items-center justify-between sm:justify-end">
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-[180px]"
          >
            <option value="all">Tutti i tipi</option>
            <option value="contract">Contratti</option>
            <option value="proposal">Proposte</option>
            <option value="invoice_pdf">Fatture PDF</option>
            <option value="meeting_note">Verbali Riunione</option>
          </Select>

          <Button onClick={handleOpenCreateModal} className="gap-1.5 shrink-0">
            <Plus className="h-4 w-4" />
            Aggiungi Documento
          </Button>
        </div>
      </div>

      {/* Grid of Documents */}
      {filteredDocuments.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 border border-dashed border-border rounded-2xl bg-card/50 text-center max-w-xl mx-auto my-8">
          <FolderOpen className="h-10 w-10 text-primary-500 mb-3 animate-pulse" />
          <h3 className="text-base font-bold text-text-primary">
            Nessun documento trovato
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            Il repository è vuoto o non ci sono documenti con i parametri di
            ricerca impostati.
          </p>
          <Button onClick={handleOpenCreateModal} className="mt-4 gap-1.5">
            <Plus className="h-4 w-4" />
            Aggiungi Primo Documento
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocuments.map((doc) => (
            <Card
              key={doc.id}
              onClick={() => setInspectDoc(doc)}
              className="group cursor-pointer hover:border-primary-500/50 hover:shadow-xs transition-all flex flex-col justify-between"
            >
              <CardHeader className="pb-3 flex flex-row justify-between items-start gap-4">
                <div className="flex items-center gap-3 truncate">
                  <div className="p-2 bg-card-muted rounded-lg border border-border group-hover:bg-primary-500/5 group-hover:border-primary-500/30 transition-all shrink-0">
                    {getDocTypeIcon(doc.type)}
                  </div>
                  <div className="truncate">
                    <CardTitle className="text-sm font-bold text-text-primary group-hover:text-primary-600 transition-colors leading-tight truncate">
                      {doc.title}
                    </CardTitle>
                    <span className="text-[10px] text-text-muted mt-0.5 block font-mono">
                      ID: {doc.id}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Link
                      href={`/explore?nodeId=${doc.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-primary-600 transition-colors"
                      title="Vedi nel Grafo"
                    >
                      <Network className="h-3.5 w-3.5" />
                    </Link>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setInspectDoc(doc);
                      }}
                      className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-primary-600 transition-colors"
                      title="Ispeziona Documento"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteDocument(e, doc.id)}
                      className="p-1 hover:bg-card-muted rounded text-text-secondary hover:text-error transition-colors"
                      title="Elimina Documento"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {getDocTypeBadge(doc.type)}
                </div>
              </CardHeader>

              <CardContent className="pb-4">
                <div className="min-h-[60px]">
                  {renderDocumentContent(doc.content, doc.type)}
                </div>
              </CardContent>

              <CardFooter className="flex items-center justify-between bg-card-muted/10 border-t border-border/10 px-4 py-2.5 text-[11px] text-text-muted font-medium rounded-b-xl">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {new Date(doc.createdAt).toLocaleDateString("it-IT", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </span>

                <div className="flex items-center gap-2">
                  {doc.fileUrl && (
                    <a
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 hover:bg-card-muted rounded text-primary-600"
                      onClick={(e) => e.stopPropagation()} // Stop inspector click
                      title="Apri file originale"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {/* Inspect Document Drawer / Sheet */}
      <Sheet
        open={!!inspectDoc}
        onOpenChange={(open) => !open && setInspectDoc(null)}
      >
        {inspectDoc && (
          <SheetContent
            hideClose
            className="max-w-xl flex flex-col h-full bg-card"
          >
            <div className="space-y-4 flex-1 flex flex-col overflow-hidden">
              <div className="pb-3 border-b border-border/40">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-card-muted rounded-lg border border-border">
                      {getDocTypeIcon(inspectDoc.type)}
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-text-primary leading-tight">
                        {inspectDoc.title}
                      </h3>
                      <p className="text-xs text-text-muted mt-1 flex items-center gap-1 font-mono">
                        ID: {inspectDoc.id} •{" "}
                        {new Date(inspectDoc.createdAt).toLocaleString("it-IT")}
                      </p>
                    </div>
                  </div>
                  <div className="shrink-0">
                    {getDocTypeBadge(inspectDoc.type)}
                  </div>
                </div>
              </div>

              {/* Document Details & File Link */}
              {inspectDoc.fileUrl && (
                <div className="p-3 bg-card-muted rounded-lg border border-border/40 text-xs flex justify-between items-center">
                  <span className="text-text-secondary font-medium">
                    Link al File Esterno:
                  </span>
                  <a
                    href={inspectDoc.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline flex items-center gap-1 font-semibold"
                  >
                    Visualizza File Originale{" "}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}

              {/* Document Content View */}
              <div className="flex-1 overflow-y-auto scrollbar-thin border border-border/60 rounded-xl bg-card p-4 space-y-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-text-secondary border-b border-border/45 pb-1 flex items-center gap-1">
                  <BookOpen className="h-3.5 w-3.5 text-primary-500" />
                  Contenuto del Documento (Indicizzato RAG)
                </h4>
                <div className="text-xs text-text-primary leading-relaxed whitespace-pre-wrap font-sans">
                  {inspectDoc.content}
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-border/40 flex justify-between items-center shrink-0">
              <Button
                variant="destructive"
                size="sm"
                onClick={(e) => {
                  handleDeleteDocument(e, inspectDoc.id);
                  setInspectDoc(null);
                }}
                className="gap-1"
              >
                <Trash2 className="h-3.5 w-3.5" /> Elimina
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setInspectDoc(null)}
              >
                Chiudi
              </Button>
            </div>
          </SheetContent>
        )}
      </Sheet>

      {/* Add Document Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Documento Knowledge Base</DialogTitle>
            <DialogDescription>
              Aggiungi un documento per arricchire la conoscenza aziendale. Il
              contenuto verrà inserito nell'indice semantico RAG.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateDocument} className="space-y-4 my-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="doc-title">Titolo Documento</Label>
                <Input
                  id="doc-title"
                  placeholder="es. Contratto di Servizi XYZ Srl"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="doc-type">Tipo Documento</Label>
                <Select
                  id="doc-type"
                  value={newType}
                  onChange={(e) => setNewType(e.target.value as any)}
                  disabled={loading}
                  required
                >
                  <option value="contract">Contratto</option>
                  <option value="proposal">Proposta Commerciale</option>
                  <option value="invoice_pdf">Fattura PDF</option>
                  <option value="meeting_note">
                    Verbale Riunione (Meeting Note)
                  </option>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="doc-fileurl">
                URL del File Esterno (Opzionale)
              </Label>
              <Input
                id="doc-fileurl"
                placeholder="es. https://storage.googleapis.com/cleos-docs/contract_123.pdf"
                value={newFileUrl}
                onChange={(e) => setNewFileUrl(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="doc-content">
                Contenuto Testuale / Trascrizione
              </Label>
              <textarea
                id="doc-content"
                placeholder="Incolla qui il contenuto testuale, la trascrizione del meeting, o i termini del contratto. Questo testo verrà analizzato per le risposte semantiche..."
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                disabled={loading}
                required
                className="flex min-h-[160px] w-full rounded-lg border border-border bg-card px-3 py-2 text-sm shadow-sm placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 text-foreground"
              />
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsCreateOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                Salva & Indicizza
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
